import { useState } from 'react'
import { ExternalLink, ImageOff, Play } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { SupportedLanguage } from '@/i18n'
import { getNewsTranslationKey, isSafeEditorialUrl, localizedNewsRecord, type NewsArticle, type NewsCopy } from '@/data/news'

function EditorialImage({ src, alt, featured = false, preserve = false }: { src: string; alt: string; featured?: boolean; preserve?: boolean }) {
  const { t } = useTranslation('common')
  const [failed, setFailed] = useState(false)
  if (failed) return <div className={`news-media-fallback${featured ? ' news-media-fallback--featured' : ''}`} role="img" aria-label={t('news.mediaUnavailable')}><ImageOff aria-hidden="true" /><span>{t('news.mediaUnavailable')}</span></div>
  return <img className={preserve ? 'news-media-preserve' : undefined} src={src} alt={alt} loading={featured ? 'eager' : 'lazy'} onError={() => setFailed(true)} />
}

export function NewsArticleView({ article, language, headingLevel = 2, copyOverride }: {
  article: NewsArticle
  language: SupportedLanguage
  headingLevel?: 1 | 2
  copyOverride?: NewsCopy
}) {
  const { t } = useTranslation('common')
  const record = localizedNewsRecord(article, language)
  const key = getNewsTranslationKey(article.id)
  const copy = copyOverride ?? t(key, { returnObjects: true }) as unknown as NewsCopy
  const media = record?.media ?? []
  const cover = record?.primaryMedia
  const gallery = media.filter((item) => item !== cover)
  const sections = copy.sections ?? []
  const showIndex = sections.filter((section) => section.heading).length >= 2
  const articleSlug = record?.slug ?? article.id
  const Heading = headingLevel === 1 ? 'h1' : 'h2'
  const scrollToSection = (id: string) => {
    const target = document.getElementById(id)
    target?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' })
    target?.focus({ preventScroll: true })
  }
  return <article className="news-article-view" lang={copy.sourceLanguage && copy.sourceLanguage !== language ? copy.sourceLanguage : undefined}>
    {cover && <div className="news-dialog-cover"><EditorialImage src={cover} alt={copy.mediaAlts[0] ?? copy.title} featured preserve={article.preservePrimaryMedia} /></div>}
    <header className="news-article-header">
      <p className="news-article-meta"><span>{t('news.categoryNews')}</span><time dateTime={record?.sourceDate ?? undefined}>{record?.sourceDate ? new Intl.DateTimeFormat(language, { dateStyle: 'long' }).format(new Date(record.sourceDate)) : ''}</time></p>
      {copy.sourceLanguage && copy.sourceLanguage !== language && <p className="news-source-note">{t('news.sourceFallback')}</p>}
      <Heading>{copy.title}</Heading>
    </header>
    <div className={`news-article-layout${showIndex ? '' : ' news-article-layout--single'}`}>
      {showIndex && <nav className="news-index" aria-label={t('news.indexLabel')}>
        <h3>{t('news.indexTitle')}</h3>
        <ol>{sections.map((section, index) => section.heading && <li key={`${article.id}-index-${index}`}><button type="button" onClick={() => scrollToSection(`news-${articleSlug}-${index}`)}>{section.heading}</button></li>)}</ol>
      </nav>}
      <div className="news-article-body">
        {sections.length === 0 && <p className="news-image-only-note">{t('news.imageOnlyDescription')}</p>}
        {sections.map((section, index) => {
          const id = section.heading ? `news-${articleSlug}-${index}` : undefined
          const externalUrl = article.externalUrls?.[language]?.[index]
          return <section key={`${article.id}-section-${index}`} aria-labelledby={id}>
            {section.heading && <h3 id={id} tabIndex={-1}>{section.heading}</h3>}
            {section.paragraphs?.map((paragraph, paragraphIndex) => <p key={`${article.id}-paragraph-${index}-${paragraphIndex}`}>{paragraph}</p>)}
            {section.items?.length ? <ul>{section.items.map((item, itemIndex) => <li key={`${article.id}-item-${index}-${itemIndex}`}>{item}</li>)}</ul> : null}
            {section.linkLabel && externalUrl && isSafeEditorialUrl(externalUrl) && <a className="news-external-link" href={externalUrl} target="_blank" rel="noopener noreferrer">{section.linkLabel}<ExternalLink aria-hidden="true" /></a>}
          </section>
        })}
        {gallery.length > 0 && <section className="news-gallery" aria-labelledby={`news-${articleSlug}-gallery`}>
          <h3 id={`news-${articleSlug}-gallery`} tabIndex={-1}>{t('news.galleryTitle')}</h3>
          <div>{gallery.map((src, index) => <figure key={src}><EditorialImage src={src} alt={copy.mediaAlts[index + 1] ?? t('news.galleryImageAlt', { index: index + 1, title: copy.title })} /></figure>)}</div>
        </section>}
        {article.videoUrls?.length ? <section className="news-video-links" aria-labelledby={`news-${articleSlug}-videos`}>
          <h3 id={`news-${articleSlug}-videos`} tabIndex={-1}>{t('news.videoTitle')}</h3>
          <div>{article.videoUrls.map((url, index) => isSafeEditorialUrl(url) && <a key={url} className="button button-secondary" href={url} target="_blank" rel="noopener noreferrer"><Play aria-hidden="true" />{copy.videoLabels?.[index] ?? t('news.videoLink', { index: index + 1 })}<ExternalLink aria-hidden="true" /></a>)}</div>
          <p>{t('news.videoNotice')}</p>
        </section> : null}
      </div>
    </div>
  </article>
}
